import { useEffect, useMemo, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import type { ActionEvent, ParseResult } from '@openuidev/react-lang'
import { Renderer } from '@openuidev/react-lang'
import { ThemeProvider } from '@openuidev/react-ui/ThemeProvider'
import { osOpenUIDarkTheme, osOpenUILightTheme, useOSColorMode } from './openuiTheme'
import { miniAppOpenUILibrary } from './library'
import { PrimaryActionContext } from './miniAppRules'
import { analyzeProgram } from './programGuard'
import type { AppUIRender } from '@/services/api/miniappTypes'

interface OpenUIRendererProps {
  ui?: AppUIRender | null
  onEvent: (actionId: string, payload?: Record<string, any>) => void
  onOpenForm: (formId: string, submitEvent?: string) => void
  onParseError?: (message: string | null) => void
}

// The OpenUI charts read --openui-stroke-default, which is not a theme key:
// axes and grid follow --border.
// Each deviation is logged once per page load (re-renders and remounts of
// the same MiniApp would otherwise repeat it).
const loggedWarnings = new Set<string>()

const scopeStyle = { '--openui-stroke-default': 'hsl(var(--border))' } as CSSProperties

function buildParseError(result: ParseResult | null) {
  if (!result) return null

  const issues: string[] = []
  if (result.meta.errors.length > 0) {
    issues.push(result.meta.errors.map((item) => item.message).join(', '))
  }
  if (result.meta.unresolved.length > 0) {
    issues.push(`references unresolved: ${result.meta.unresolved.join(', ')}`)
  }

  return issues.length > 0 ? issues.join(' | ') : null
}

export default function OpenUIRenderer({
  ui,
  onEvent,
  onOpenForm,
  onParseError,
}: OpenUIRendererProps) {
  const initialState = useMemo(
    () => ui?.initial_state ?? {},
    [ui?.initial_state],
  )

  const mode = useOSColorMode()
  const { t } = useTranslation()
  const program = ui?.program
  // OSA-09: one primary action per screen, no free colour; the deviations
  // are logged in development (the compiler does not enforce them yet).
  const guard = useMemo(() => analyzeProgram(program), [program])
  const showWarnings = import.meta.env.DEV && guard.warnings.length > 0
  useEffect(() => {
    if (!import.meta.env.DEV) return
    for (const warning of guard.warnings) {
      if (loggedWarnings.has(warning)) continue
      loggedWarnings.add(warning)
      console.warn(`[MiniApp OpenUI] ${warning}`)
    }
  }, [guard])

  if (!ui?.program) return null

  return (
    <ThemeProvider mode={mode} lightTheme={osOpenUILightTheme} darkTheme={osOpenUIDarkTheme} cssSelector=".am-openui-scope">
      <div className="am-openui-scope" style={scopeStyle}>
      {showWarnings ? (
        <div role="note" className="mb-4 rounded-2xl border border-warning bg-warning-soft px-4 py-3 text-sm text-warning" data-openui-warnings={guard.warnings.length}>
          <p className="font-semibold">{t('common.miniappDevWarnings')}</p>
          <ul className="mt-1 list-disc pl-5">
            {guard.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <PrimaryActionContext.Provider value={guard.primaryKey}>
      <Renderer
        response={ui.program}
        library={miniAppOpenUILibrary}
        initialState={initialState}
        onAction={(event: ActionEvent) => {
          if (event.type === 'form_link') {
            onOpenForm(
              typeof event.params?.formId === 'string' ? event.params.formId : '',
              typeof event.params?.submitEvent === 'string'
                ? event.params.submitEvent
                : undefined,
            )
            return
          }

          const actionId =
            typeof event.params?.actionId === 'string'
              ? event.params.actionId
              : typeof event.params?.eventId === 'string'
                ? event.params.eventId
                : ''
          if (!actionId) return

          const payload: Record<string, any> = {}
          if ('value' in event.params) {
            payload.value = event.params.value
          }
          if ('index' in event.params) {
            payload.index = event.params.index
          }

          onEvent(actionId, Object.keys(payload).length > 0 ? payload : undefined)
        }}
        onParseResult={(result) => onParseError?.(buildParseError(result))}
      />
      </PrimaryActionContext.Provider>
      </div>
    </ThemeProvider>
  )
}
